// Shared utility functions for construction planning

import { VarianceResult } from './types';

export const calculateVariance = (originalDate: string, revisedDate: string): VarianceResult => {
  if (!revisedDate || !originalDate) return { days: 0, status: 'on-track' };

  const original = new Date(originalDate);
  const revised = new Date(revisedDate);

  if (isNaN(original.getTime()) || isNaN(revised.getTime())) {
    return { days: 0, status: 'on-track' };
  }

  const diffTime = revised.getTime() - original.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  let status: 'on-track' | 'at-risk' | 'delayed' | 'ahead' = 'on-track';
  if (diffDays > 7) status = 'delayed';
  else if (diffDays > 0) status = 'at-risk';
  else if (diffDays < 0) status = 'ahead';

  return { days: diffDays, status };
};

export const getVarianceColor = (status: string): string => {
  const colors: Record<string, string> = {
    'on-track': 'text-green-600 bg-green-50',
    'at-risk': 'text-yellow-600 bg-yellow-50',
    'delayed': 'text-red-600 bg-red-50',
    'ahead': 'text-blue-600 bg-blue-50'
  };
  return colors[status] || colors['on-track'];
};

export const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    'Completed': 'text-green-600 bg-green-50',
    'In Progress': 'text-blue-600 bg-blue-50',
    'Planned': 'text-gray-600 bg-gray-50',
    'Started': 'text-yellow-600 bg-yellow-50'
  };
  return colors[status] || colors['Planned'];
};

export const formatCurrency = (amount: number): string => {
  return `₹${amount.toLocaleString()}`;
};

// Format date to DD-MM-YYYY (human readable, no time)
export const formatDate = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return '-';
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return '-';
  
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}-${month}-${year}`;
};

// Format date to short format (e.g., "12 Jan 2024")
export const formatDateShort = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return '-';
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const calculateProgress = (completed: number, total: number): number => {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
};

